/**
 * DDI (Country Dial Code) Utilities
 * 
 * Provides optimized lookup and phone number normalization using country dial codes.
 * Uses Map-based caching for O(1) lookup performance.
 */

import ddiData from '../data/ddi-paises.json';

export interface CountryDDI {
    name: string;
    dial_code: string;
    code: string;
}

// Type-safe country data
const countries: CountryDDI[] = ddiData as CountryDDI[];

// Optimized lookup maps (created once at module initialization)
const dialCodeByCountryCode = new Map<string, string>();
const dialCodeByCountryName = new Map<string, string>();

// Initialize lookup maps
countries.forEach((country) => {
    // Map by country code (e.g., "BR", "US") - case insensitive
    dialCodeByCountryCode.set(country.code.toUpperCase(), country.dial_code);

    // Map by country name (e.g., "Brazil", "United States") - case insensitive
    dialCodeByCountryName.set(country.name.toLowerCase(), country.dial_code);
});

/**
 * Get dial code by country code (2-letter code)
 * @param code - Country code (e.g., "BR", "US", "mx") - case insensitive
 * @returns Dial code with + sign (e.g., "+55") or null if not found
 */
export function getDialCodeByCountryCode(code: string): string | null {
    return dialCodeByCountryCode.get(code.toUpperCase()) || null;
}

/**
 * Get dial code by country name (English name)
 * @param name - Country name (e.g., "Brazil", "United States") - case insensitive
 * @returns Dial code with + sign (e.g., "+55") or null if not found
 */
export function getDialCodeByCountryName(name: string): string | null {
    return dialCodeByCountryName.get(name.toLowerCase()) || null;
}

/**
 * Get dial code by country identifier (code or name)
 * @param identifier - Country code or name
 * @returns Dial code with + sign (e.g., "+55") or null if not found
 */
export function getDialCode(identifier: string): string | null {
    // Try by code first (faster)
    const byCode = getDialCodeByCountryCode(identifier);
    if (byCode) return byCode;

    // Try by name
    return getDialCodeByCountryName(identifier);
}

/**
 * Normalize a phone number by adding country code if missing
 * @param phone - Phone number in any format
 * @param countryIdentifier - Country code (e.g., "BR") or country name (e.g., "Brazil")
 * @returns Normalized phone number with country code or null if country not found
 */
export function normalizePhoneNumber(phone: string | null | undefined, countryIdentifier: string): string | null {
    if (!phone) return null;

    // Get the dial code for the country
    const dialCode = getDialCode(countryIdentifier);
    if (!dialCode) {
        console.warn(`[DDI] Country not found: ${countryIdentifier}`);
        return phone; // Return original if country not found
    }

    // Remove all non-numeric characters except the leading +
    let cleaned = phone.trim();

    // Check if phone already starts with the dial code
    if (cleaned.startsWith(dialCode)) {
        // Already has the correct dial code
        return cleaned;
    }

    // Check if phone starts with + but different dial code
    if (cleaned.startsWith('+')) {
        // Phone already has a dial code, don't modify
        console.warn(`[DDI] Phone already has dial code: ${cleaned}`);
        return cleaned;
    }

    // Remove any remaining non-numeric characters
    cleaned = cleaned.replace(/\D/g, '');

    // Special handling for France (FR +33): remove leading 0 from local format
    // French local: 06 12 34 56 78 (10 digits)
    // French international: +33 6 12 34 56 78 (9 digits, no leading 0)
    if (dialCode === '+33' && cleaned.startsWith('0') && cleaned.length === 10) {
        cleaned = cleaned.substring(1); // Remove leading 0
        console.log(`[DDI] France: Removed leading 0 from local number ${phone} -> ${cleaned}`);
    }

    // Check if the cleaned number starts with the dial code without the +
    const dialCodeWithoutPlus = dialCode.substring(1);
    if (cleaned.startsWith(dialCodeWithoutPlus)) {
        // Number already contains the dial code, just add the +
        return '+' + cleaned;
    }

    // Add the dial code
    return dialCode + cleaned;
}

/**
 * Validate if a phone number has a valid country code
 * @param phone - Phone number to validate
 * @returns true if phone has a valid country code, false otherwise
 */
export function hasValidDialCode(phone: string | null | undefined): boolean {
    if (!phone) return false;

    const cleaned = phone.trim();
    if (!cleaned.startsWith('+')) return false;

    // Check if any country has this dial code
    return countries.some(country => cleaned.startsWith(country.dial_code));
}

/**
 * Get all available countries with their dial codes
 * @returns Array of all country DDI data
 */
export function getAllCountries(): CountryDDI[] {
    return countries;
}

/**
 * Get country name from country code
 * @param code - Country code (e.g., "BR", "US", "FR") - case insensitive
 * @returns Country name in English or null if not found
 */
export function getCountryNameFromCode(code: string): string | null {
    const country = countries.find(c => c.code.toUpperCase() === code.toUpperCase());
    return country ? country.name : null;
}

/**
 * Normalize phone number for storage (with DDI, without +)
 * This ensures consistent format for database storage and API comparisons
 * @param phone - Phone number to normalize
 * @param countryIdentifier - Country code or name
 * @returns Normalized phone (e.g., "5511999999999") or original if normalization fails
 */
export function normalizePhoneForStorage(phone: string | null | undefined, countryIdentifier: string): string | null {
    if (!phone) return null;

    // First normalize with DDI (adds + if needed)
    const normalized = normalizePhoneNumber(phone, countryIdentifier);
    if (!normalized) return phone;

    // Remove the + sign for storage
    return normalized.replace(/^\+/, '');
}

